import { createAsyncThunk } from "@reduxjs/toolkit";

import apiRequest from "../../../services/api";
import { setApiValue } from "../../../utils/shared/apiValues";
import { typePostMap } from "../../../utils/shared/variables";

// Filters
export const fetchFeedFilters = createAsyncThunk(
  "feed/fetch_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    const feedFilters = await apiRequest.get(
      `v1/feed_editorials/filters/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return feedFilters.data;
  }
);

// Feed category filter
export const fetchCategoryFilters = createAsyncThunk(
  "programs/fetch_category_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.learning_type) {
      query = query + `learning_type=${body.learningType}&`;
    }
    const programsFilters = await apiRequest.get(
      `v2/programs/learning/filters/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);

// Fetch feed editorial
export const fetchFeedEditorial = createAsyncThunk(
  "feed/fetch_feed_editorial",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.selectedFeeds) {
      query = query + `selected_feeds=${body.selectedFeeds}&`;
    }
    if (body.filter_type) {
      query = query + `filter_type=${body.filter_type}&`;
    }
    if (body.organization) {
      query = query + `organization=${body.organization}&`;
    }
    if (body.start_date) {
      query = query + `start_date=${body.start_date}&`;
    }
    if (body.end_date) {
      query = query + `end_date=${body.end_date}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.primary_tags) {
      query = query + `primary_tags=${body.primary_tags}&`;
    }
    if (body.secondary_tags) {
      query = query + `secondary_tags=${body.secondary_tags}&`;
    }
    if (body.categories) {
      query = query + `categories=${body.categories}&`;
    }
    if (body.user_role) {
      query = query + `user_role=${body.user_role}&`;
    }
    if (body.internal_notes) {
      query = query + `internal_notes=${body.internal_notes}&`;
    }
    if (body.orderby) {
      query = query + `ordering=${body.orderby}&`;
    }
    if (body.status) {
      query = query + `status=${body.status}&`;
    }
    const feedEditoral = await apiRequest.get(`v1/feed_editorials/?${query}`, {
      headers: { organization: body.userOrganization?.org_id },
    });
    return feedEditoral.data;
  }
);

export const fetchFeedAssignments = createAsyncThunk(
  "feed/assignments",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.user_type) {
      query =
        query + `user_type=${body.user_type === "All" ? "" : body.user_type}&`;
    }
    if (body.post_type) {
      query =
        query +
        `post_type=${body.post_type === "All" ? "" : typePostMap[body.post_type]}&`;
    }
    if (body.status) {
      query = query + `status=${body.status === "All" ? "" : body.status}&`;
    }
    if (
      body.organization &&
      body.organization !== "All" &&
      body.organization.length > 0
    ) {
      query = query + `organization=${body.organization}&`;
    }
    if (body.clinic) {
      query = query + `clinic=${body.clinic === "All" ? "" : body.clinic}&`;
    }
    if (body.role) {
      query = query + `role=${body.role === "All" ? "" : body.role}&`;
    }
    if (body.user) {
      query = query + `user_email=${body.user === "All" ? "" : body.user}&`;
    }
    if (body.orderby) {
      query = query + `ordering=${body.orderby}&`;
    } else {
      query = query + `ordering=-created_at&`;
    }
    if (body.group) {
      query = query + `group=${body.user === "All" ? "" : body.group}&`;
    }
    if (body.search) {
      query = query + `feed__title=${body.search}`;
    }
    const feedEditoral = await apiRequest.get(`v2/feed/assignments/?${query}`);

    return feedEditoral.data;
  }
);

export const fetchFeedAssignmentId = createAsyncThunk(
  "feed/assignment/:id",
  async (AssignmentId) => {
    const feedAssignment = await apiRequest.get(
      `v2/feed/assignment/${AssignmentId}/`
    );
    return feedAssignment.data;
  }
);

// Fetch feed details
export const fetchFeedDetails = createAsyncThunk(
  "feed/fetch_feed_details",
  async (body) => {
    const feedDetails = await apiRequest.get(
      `v1/feed_editorials/detail/?post_id=${body.postId}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return feedDetails.data;
  }
);

export const fetchFeedAssignmentDetails = createAsyncThunk(
  "feed/fetch_feed_details",
  async (body) => {
    const feedDetails = await apiRequest.get(
      `v2/feed/assignment/${body.postId}`
    );
    return feedDetails.data;
  }
);

// Create feed editorial
export const createFeedEditorial = createAsyncThunk(
  "feed/create_feed_editorial",
  async (body, { rejectWithValue }) => {
    try {
      const newFeedEditorial = await apiRequest.post(
        `v1/feed_editorials/`,
        body.newFeedEditorialData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
                progressEvent.target.getResponseHeader(
                  "x-decompressed-content-length"
                );
            const uploadProgress = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (uploadProgress !== null) {
              setApiValue({ type: "feedUploadProgress", data: uploadProgress });
            }
          },
        }
      );
      return newFeedEditorial;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Update feed editorial
export const updateFeedEditorial = createAsyncThunk(
  "feed/update_feed_editorial",
  async (body, { rejectWithValue }) => {
    try {
      const newFeedEditorial = await apiRequest.patch(
        `v1/feed_editorials/?post_id=${body.postId}`,
        body.newFeedEditorialData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
                progressEvent.target.getResponseHeader(
                  "x-decompressed-content-length"
                );
            const uploadProgress = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (uploadProgress !== null) {
              setApiValue({ type: "feedUploadProgress", data: uploadProgress });
            }
          },
        }
      );
      return newFeedEditorial;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
// Delete feed editorial
export const deleteFeedEditorial = createAsyncThunk(
  "feed/delete_feed_editorial",
  async (body, { rejectWithValue }) => {
    try {
      const feedDelete = await apiRequest.delete(
        `v1/feed_editorials/?post_id=` + body.postId,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return feedDelete.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
// Duplicate feed editorial
export const duplicateFeedEditorial = createAsyncThunk(
  "feed/duplicate_feed_editorial",
  async (body) => {
    const feedDuplicate = await apiRequest.post(
      `v1/feed_editorials/duplicate/?post_id=` + body.postId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return feedDuplicate;
  }
);

export const assignFeed = createAsyncThunk(
  "feed/assignment",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(
        `v2/feed/${id}/assignments/`,
        body
      );
      return response.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchFeedInternalNotesFilters = createAsyncThunk(
  "feed/fetch_feed_internal_notes_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}`;
    if (body.page_size) {
      query = query + `&page_size=${body.page_size}`;
    }
    if (body.search) {
      query = query + `&search=${body.search}`;
    }
    if (body.ordering) {
      query = query + `&ordering=${body.ordering}`;
    }
    if (body.post_type) {
      query = query + `&post_type=${body.post_type}`;
    }
    const programsInternalNotesFilters = await apiRequest.get(
      `v2/feeds/filters/internal_notes/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsInternalNotesFilters.data;
  }
);

export const deleteFeedAssignment = createAsyncThunk(
  "feed/delete_assignment",
  async (body, { rejectWithValue }) => {
    try {
      const feedDelete = await apiRequest.delete(
        `v2/feed/assignment/${body.id}/`,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return feedDelete.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
export const bulkUploadDocument = createAsyncThunk(
  "media/bulk_upload",
  async (body, thunkAPI) => {
    try {
      const formData = new FormData();
      formData.append("post_type", body.post_type);
      body.files.forEach((file) => formData.append("files", file));

      const response = await apiRequest.post(
        "v2/media/bulk_upload/",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Organization: body.userOrganization?.org_id,
          },
        }
      );

      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error?.response?.data || "Upload failed");
    }
  }
);
